import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import TimeSlider from './TimeSlider';
import Sidebar from './Sidebar';
import HoverInfo from './HoverInfo';
import AddressLookup from './AddressLookup';
import SchoolFilter from './SchoolFilter';
import { normalizeSchoolName, createSchoolKey } from './SchoolFilter';

const MapContainer = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [lng, setLng] = useState(-77.1);
  const [lat, setLat] = useState(39.1);
  const [zoom, setZoom] = useState(10);
  const [visibleLayers, setVisibleLayers] = useState({
    elementary: true,
    highSchool: true,
    middleSchool: true
  });
  
  // Multi-year state - only show representative years
  const availableYears = ['1999', '2003', '2008-2009', '2013-2014', '2018-2019', '2024-2025'];
  const displayYears = ['1999', '2003', '2008', '2013', '2018', '2024']; // Simplified display
  const [currentYear, setCurrentYear] = useState('1999');
  
  // MOBILE: Responsive states
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState('map'); // 'map', 'search', 'filter', 'layers'
  
  // MOBILE: Enhanced viewport persistence state (simplified)
  const [currentViewport, setCurrentViewport] = useState({
    center: [-77.1, 39.1],
    zoom: 10
  });
  
  // About modal state
  const [showAboutModal, setShowAboutModal] = useState(false);
  
  // Sidebar width state (managed by Sidebar component)
  const [sidebarWidth, setSidebarWidth] = useState(320);
  
  const [isLoading, setIsLoading] = useState(false);
  const [yearData, setYearData] = useState({});
  
  // School filtering state - store persistent selections by NORMALIZED name keys
  const [selectedSchoolNames, setSelectedSchoolNames] = useState(new Set());
  const [isFiltering, setIsFiltering] = useState(false);
  
  // Color coding state
  const [colorBySchool, setColorBySchool] = useState(false);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [schoolColorMap, setSchoolColorMap] = useState(new Map());

  // CRITICAL: Add persistent color state that survives year changes
  const [persistentColorState, setPersistentColorState] = useState({
    isActive: false,
    colorMap: new Map(),
    lastUpdated: null
  });

  const schoolColors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
    '#FECA57', '#FF9FF3', '#54A0FF', '#5F27CD'
  ];

  // MOBILE: Detect mobile and tablet devices
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const userAgent = navigator.userAgent;
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      setIsMobile(width <= 768 || isMobileUserAgent);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Calculate dynamic timeline position
  const timelineLeftMargin = isMobile ? 16 : (sidebarWidth + 20);

  // STABLE: Map display year to actual data year - use useMemo to prevent recreations
  const getDataYear = useMemo(() => {
    const yearMap = {
      '1999': '1999',
      '2003': '2003', 
      '2008': '2008-2009',
      '2013': '2013-2014',
      '2018': '2018-2019',
      '2024': '2024-2025'
    };
    return (displayYear) => yearMap[displayYear] || displayYear;
  }, []);

  // STABLE: Get display year from data year - use useMemo to prevent recreations
  const getDisplayYear = useMemo(() => {
    const reverseMap = {
      '1999': '1999',
      '2003': '2003',
      '2008-2009': '2008', 
      '2013-2014': '2013',
      '2018-2019': '2018',
      '2024-2025': '2024'
    };
    return (dataYear) => reverseMap[dataYear] || dataYear;
  }, []);

  // Calculate polygon area from coordinates using Shoelace formula
  const calculatePolygonArea = useCallback((coordinates) => {
    if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) {
      return 0;
    }

    // Handle both Polygon and MultiPolygon geometries
    let totalArea = 0;
    
    // For Polygon: coordinates is an array of rings, we want the outer ring (first one)
    // For MultiPolygon: coordinates is an array of polygons, each with rings
    const rings = Array.isArray(coordinates[0][0]) ? 
      (typeof coordinates[0][0][0] === 'number' ? [coordinates] : coordinates) : // Polygon vs MultiPolygon
      coordinates;

    rings.forEach(polygon => {
      const ring = Array.isArray(polygon[0][0]) ? polygon[0] : polygon; // Get outer ring
      
      if (ring.length < 3) return; // Need at least 3 points for a polygon

      let area = 0;
      const n = ring.length;
      
      for (let i = 0; i < n - 1; i++) { // -1 because last point = first point in closed polygon
        const [x1, y1] = ring[i];
        const [x2, y2] = ring[i + 1];
        
        // Shoelace formula
        area += (x1 * y2) - (x2 * y1);
      }
      
      totalArea += Math.abs(area) / 2;
    });

    // Convert from decimal degrees to square meters (approximate)
    // This is a rough approximation - for precise calculations you'd need proper projection
    const lat = coordinates[0] && coordinates[0][0] && coordinates[0][0][1] ? coordinates[0][0][1] : 39; // Fallback to MD latitude
    const metersPerDegreeLat = 111320; // meters per degree latitude (constant)
    const metersPerDegreeLng = 111320 * Math.cos(lat * Math.PI / 180); // varies by latitude
    
    const areaInSquareMeters = totalArea * metersPerDegreeLat * metersPerDegreeLng;
    const areaInSquareFeet = areaInSquareMeters * 10.764; // Convert to square feet
    const areaInAcres = areaInSquareFeet / 43560; // Convert to acres
    
    return areaInAcres;
  }, []);

  // Point-in-polygon algorithm (ray casting) - stable function
  const isPointInPolygon = useCallback((point, polygon) => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  }, []);

  // STABLE: Spatial lookup function - use useCallback with proper dependencies
  const findClusterForPoint = useCallback((lng, lat, clusterData) => {
    if (!clusterData || !clusterData.features) {
      return null;
    }
    
    for (let i = 0; i < clusterData.features.length; i++) {
      const cluster = clusterData.features[i];
      
      if (cluster.geometry.type === 'Polygon') {
        if (isPointInPolygon([lng, lat], cluster.geometry.coordinates[0])) {
          return cluster.properties;
        }
      } else if (cluster.geometry.type === 'MultiPolygon') {
        for (const polygon of cluster.geometry.coordinates) {
          if (isPointInPolygon([lng, lat], polygon[0])) {
            return cluster.properties;
          }
        }
      }
    }
    
    return null;
  }, [isPointInPolygon]);

  // STABLE: Extract school name - use useCallback to prevent recreations
  const extractSchoolName = useCallback((feature, index, type) => {
    let rawName;
    
    if (type === 'elementary' || type === 'middleSchool') {
      rawName = feature.properties?.S_NAME || 
               feature.properties?.SCHNAME || 
               feature.properties?.NAME ||
               feature.properties?.SCHOOLNAME ||
               feature.properties?.School_Name ||
               feature.properties?.school_name ||
               // 2024-2025 specific fields
               feature.properties?.ES_csv_S_N ||  // Elementary school name in 2024-2025
               feature.properties?.MS_csv_S_N ||  // Middle school name in 2024-2025 (if exists)
               feature.properties?.ES_csv_P_N ||  // Alternative elementary field
               feature.properties?.MS_csv_P_N ||  // Alternative middle field
               `${type === 'elementary' ? 'Elementary' : 'Middle'} School ${index + 1}`;
    } else { // highSchool
      rawName = feature.properties?.CLUSTER || 
               feature.properties?.C_NAME || 
               feature.properties?.S_NAME || 
               feature.properties?.NAME ||
               feature.properties?.SCHOOLNAME ||
               feature.properties?.School_Name ||
               feature.properties?.school_name ||
               feature.properties?.SCHNAME ||
               // 2024-2025 specific fields
               feature.properties?.HS_csv_S_N ||  // High school name in 2024-2025
               feature.properties?.HS_csv_C_N ||  // High school cluster name
               feature.properties?.HS_csv_P_N ||  // Alternative high school field
               `High School ${index + 1}`;
    }
    
    return rawName;
  }, []);

  // STABLE: Smart filtering - use useCallback with proper dependencies
  const applySmartFiltering = useCallback((data, schoolNamesSet) => {
    if (!data || schoolNamesSet.size === 0) {
      return data;
    }

    // Create a set of normalized school keys to match against
    const normalizedSelections = new Set();
    schoolNamesSet.forEach(schoolName => {
      if (schoolName.includes(':')) {
        const [type, name] = schoolName.split(':');
        const normalizedKey = createSchoolKey(name, type);
        normalizedSelections.add(normalizedKey);
      } else {
        normalizedSelections.add(schoolName);
      }
    });

    // Helper function to check if a school should be included
    const shouldIncludeSchool = (rawName, type) => {
      const normalizedKey = createSchoolKey(rawName, type);
      return normalizedSelections.has(normalizedKey);
    };

    // Filter data using normalized matching
    const filteredData = {
      elementary: {
        type: 'FeatureCollection',
        features: (data.elementary?.features || []).filter((feature, index) => {
          const rawName = extractSchoolName(feature, index, 'elementary');
          return shouldIncludeSchool(rawName, 'elementary');
        })
      },
      middleSchool: {
        type: 'FeatureCollection',
        features: (data.middleSchool?.features || []).filter((feature, index) => {
          const rawName = extractSchoolName(feature, index, 'middleSchool');
          return shouldIncludeSchool(rawName, 'middleSchool');
        })
      },
      highSchool: {
        type: 'FeatureCollection',
        features: (data.highSchool?.features || []).filter((feature, index) => {
          const rawName = extractSchoolName(feature, index, 'highSchool');
          return shouldIncludeSchool(rawName, 'highSchool');
        })
      }
    };

    return filteredData;
  }, [extractSchoolName]);

  // STABLE: Create tooltip HTML - use useCallback to prevent recreations

  // STABLE: Map color update function - FIXED Mapbox expression building
  const updateLayerColorsWithColorMap = useCallback((colorMap, useSchoolColors) => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    try {
      const data = yearData[currentYear];
      if (!data) return;

      console.log('Updating layer colors:', { useSchoolColors, colorMapSize: colorMap.size });

      // Update elementary layer colors
      if (map.current.getLayer('elementary-fill') && data.elementary?.features) {
        if (useSchoolColors && colorMap.size > 0) {
          const colorExpression = ['case'];
          let hasConditions = false;
          
          data.elementary.features.forEach((feature, index) => {
            const rawName = extractSchoolName(feature, index, 'elementary');
            const normalizedName = normalizeSchoolName(rawName, 'elementary');
            const normalizedKey = `elementary:${normalizedName}`;
            
            let assignedColor = colorMap.get(normalizedKey);
            if (!assignedColor) {
              const originalKey = createSchoolKey(rawName, 'elementary');
              assignedColor = colorMap.get(originalKey);
            }
            
            if (assignedColor && rawName) {
              // Add multiple field checks for the same color
              if (feature.properties.S_NAME === rawName) {
                colorExpression.push(['==', ['get', 'S_NAME'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
              if (feature.properties.SCHNAME === rawName) {
                colorExpression.push(['==', ['get', 'SCHNAME'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
              if (feature.properties.NAME === rawName) {
                colorExpression.push(['==', ['get', 'NAME'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
              if (feature.properties.ES_csv_S_N === rawName) {
                colorExpression.push(['==', ['get', 'ES_csv_S_N'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
            }
          });
          
          // CRITICAL: Only apply expression if we have conditions, otherwise use default
          if (hasConditions) {
            colorExpression.push('#3B82F6'); // Default fallback
            map.current.setPaintProperty('elementary-fill', 'fill-color', colorExpression);
          } else {
            map.current.setPaintProperty('elementary-fill', 'fill-color', '#3B82F6');
          }
        } else {
          map.current.setPaintProperty('elementary-fill', 'fill-color', '#3B82F6');
        }
      }

      // Update middle school layer colors
      if (map.current.getLayer('middleschool-fill') && data.middleSchool?.features) {
        if (useSchoolColors && colorMap.size > 0) {
          const colorExpression = ['case'];
          let hasConditions = false;
          
          data.middleSchool.features.forEach((feature, index) => {
            const rawName = extractSchoolName(feature, index, 'middleSchool');
            const normalizedName = normalizeSchoolName(rawName, 'middleSchool');
            const normalizedKey = `middleSchool:${normalizedName}`;
            
            let assignedColor = colorMap.get(normalizedKey);
            if (!assignedColor) {
              const originalKey = createSchoolKey(rawName, 'middleSchool');
              assignedColor = colorMap.get(originalKey);
            }
            
            if (assignedColor && rawName) {
              // Add multiple field checks for the same color
              if (feature.properties.S_NAME === rawName) {
                colorExpression.push(['==', ['get', 'S_NAME'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
              if (feature.properties.SCHNAME === rawName) {
                colorExpression.push(['==', ['get', 'SCHNAME'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
              if (feature.properties.NAME === rawName) {
                colorExpression.push(['==', ['get', 'NAME'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
              if (feature.properties.MS_csv_S_N === rawName) {
                colorExpression.push(['==', ['get', 'MS_csv_S_N'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
            }
          });
          
          // CRITICAL: Only apply expression if we have conditions
          if (hasConditions) {
            colorExpression.push('#10B981'); // Default fallback
            map.current.setPaintProperty('middleschool-fill', 'fill-color', colorExpression);
          } else {
            map.current.setPaintProperty('middleschool-fill', 'fill-color', '#10B981');
          }
        } else {
          map.current.setPaintProperty('middleschool-fill', 'fill-color', '#10B981');
        }
      }

      // Update high school layer colors
      if (map.current.getLayer('highschool-fill') && data.highSchool?.features) {
        if (useSchoolColors && colorMap.size > 0) {
          const colorExpression = ['case'];
          let hasConditions = false;
          
          data.highSchool.features.forEach((feature, index) => {
            const rawName = extractSchoolName(feature, index, 'highSchool');
            const normalizedName = normalizeSchoolName(rawName, 'highSchool');
            const normalizedKey = `highSchool:${normalizedName}`;
            
            let assignedColor = colorMap.get(normalizedKey);
            if (!assignedColor) {
              const originalKey = createSchoolKey(rawName, 'highSchool');
              assignedColor = colorMap.get(originalKey);
            }
            
            if (assignedColor && rawName) {
              // Add multiple field checks for the same color
              if (feature.properties.CLUSTER === rawName) {
                colorExpression.push(['==', ['get', 'CLUSTER'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
              if (feature.properties.C_NAME === rawName) {
                colorExpression.push(['==', ['get', 'C_NAME'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
              if (feature.properties.S_NAME === rawName) {
                colorExpression.push(['==', ['get', 'S_NAME'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
              if (feature.properties.NAME === rawName) {
                colorExpression.push(['==', ['get', 'NAME'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
              if (feature.properties.HS_csv_S_N === rawName) {
                colorExpression.push(['==', ['get', 'HS_csv_S_N'], rawName]);
                colorExpression.push(assignedColor);
                hasConditions = true;
              }
            }
          });
          
          // CRITICAL: Only apply expression if we have conditions
          if (hasConditions) {
            colorExpression.push('#EF4444'); // Default fallback
            map.current.setPaintProperty('highschool-fill', 'fill-color', colorExpression);
          } else {
            map.current.setPaintProperty('highschool-fill', 'fill-color', '#EF4444');
          }
        } else {
          map.current.setPaintProperty('highschool-fill', 'fill-color', '#EF4444');
        }
      }

      console.log('Layer colors updated successfully');
    } catch (error) {
      console.error('Error updating layer colors:', error);
    }
  }, [currentYear, yearData, extractSchoolName]);

  // STABLE: Create color assignments - use useCallback with stable dependencies
  const assignSchoolColors = useCallback((schools) => {
    const newColorMap = new Map();
    
    schools.forEach((school, index) => {
      const schoolKey = school.schoolKey || createSchoolKey(school.name, school.type);
      const colorIndex = index % schoolColors.length;
      newColorMap.set(schoolKey, schoolColors[colorIndex]);
    });
    
    return newColorMap;
  }, [schoolColors]);

  // ENHANCED: Toggle color mode - PERSISTENT color state management with logging
  const toggleColorMode = useCallback(() => {
    setColorBySchool(prev => {
      const newColorBySchool = !prev;
      console.log('Toggling color mode:', { from: prev, to: newColorBySchool });
      
      if (newColorBySchool && selectedSchools.length > 0) {
        // Enabling color mode - create/restore color assignments
        const newColorMap = assignSchoolColors(selectedSchools);
        console.log('Creating new color map:', newColorMap);
        setSchoolColorMap(newColorMap);
        
        // Apply colors immediately without waiting for effect
        setTimeout(() => {
          if (map.current && map.current.isStyleLoaded()) {
            updateLayerColorsWithColorMap(newColorMap, newColorBySchool);
          }
        }, 0);
      }
      // IMPORTANT: When disabling color mode, keep the color map but don't apply colors
      // This allows quick re-enabling without losing color assignments
      
      return newColorBySchool;
    });
  }, [selectedSchools, assignSchoolColors, updateLayerColorsWithColorMap]);

  // STABLE: Update color assignments when schools change - ENHANCED persistence
  useEffect(() => {
    // Only update colors if we're in color mode AND have schools selected
    if (selectedSchools.length > 0 && colorBySchool) {
      const newColorMap = new Map();
      
      selectedSchools.forEach((school, index) => {
        const normalizedName = normalizeSchoolName(school.name, school.type);
        const normalizedKey = `${school.type}:${normalizedName}`;
        const colorIndex = index % schoolColors.length;
        newColorMap.set(normalizedKey, schoolColors[colorIndex]);
        
        if (school.schoolKey) {
          newColorMap.set(school.schoolKey, schoolColors[colorIndex]);
        }
      });
      
      // Always update color map when schools change in color mode
      setSchoolColorMap(newColorMap);
    } else if (selectedSchools.length === 0) {
      // Clear colors when no schools selected
      setSchoolColorMap(new Map());
    }
    // IMPORTANT: Don't clear colors when colorBySchool is false - keep them for potential re-enable
  }, [selectedSchools, colorBySchool]); // STABLE: Only depend on selected schools and color mode

  // STABLE: Update colors when map state changes - ENHANCED with year change detection
  useEffect(() => {
    if (map.current && map.current.isStyleLoaded() && yearData[currentYear]) {
      // Use a timeout to prevent immediate updates during state changes
      const timeoutId = setTimeout(() => {
        console.log('Applying colors to map:', { colorBySchool, colorMapSize: schoolColorMap.size });
        updateLayerColorsWithColorMap(schoolColorMap, colorBySchool);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [schoolColorMap, colorBySchool, currentYear, updateLayerColorsWithColorMap]);

  // DESKTOP LOGIC APPLIED TO ALL: Handle school filtering (same as desktop) - ENHANCED color persistence
  const handleSchoolsFilter = useCallback((selectedSchoolsArray) => {
    // Store previous color state to maintain consistency
    const wasColoringBySchool = colorBySchool;
    const previousColorMap = new Map(schoolColorMap);
    
    setSelectedSchools(selectedSchoolsArray);
    
    const schoolNames = new Set(selectedSchoolsArray.map(school => 
      school.schoolKey || createSchoolKey(school.name, school.type)
    ));
    
    setSelectedSchoolNames(schoolNames);
    setIsFiltering(selectedSchoolsArray.length > 0);

    // ENHANCED: Smart color mode management
    if (selectedSchoolsArray.length === 0) {
      // No schools selected - disable color mode and clear colors
      setColorBySchool(false);
      setSchoolColorMap(new Map());
    } else if (selectedSchoolsArray.length > 12) {
      // Too many schools - disable color mode but keep the toggle available
      setColorBySchool(false);
    } else if (wasColoringBySchool && selectedSchoolsArray.length <= 12) {
      // Was coloring and still have valid number - maintain color mode
      setColorBySchool(true);
      
      // Reassign colors to new selection, preserving existing assignments where possible
      const newColorMap = new Map();
      selectedSchoolsArray.forEach((school, index) => {
        const normalizedName = normalizeSchoolName(school.name, school.type);
        const normalizedKey = `${school.type}:${normalizedName}`;
        
        // Try to preserve existing color assignment
        let assignedColor = previousColorMap.get(normalizedKey);
        if (!assignedColor && school.schoolKey) {
          assignedColor = previousColorMap.get(school.schoolKey);
        }
        
        // Assign new color if no existing assignment
        if (!assignedColor) {
          const colorIndex = index % schoolColors.length;
          assignedColor = schoolColors[colorIndex];
        }
        
        newColorMap.set(normalizedKey, assignedColor);
        if (school.schoolKey) {
          newColorMap.set(school.schoolKey, assignedColor);
        }
      });
      
      setSchoolColorMap(newColorMap);
    }

    if (!map.current || !yearData[currentYear]) return;

    const data = yearData[currentYear];

    try {
      let filteredData;
      
      if (schoolNames.size === 0) {
        filteredData = data;
      } else {
        filteredData = applySmartFiltering(data, schoolNames);
      }
      
      // DESKTOP APPROACH: Simple source updates without any special handling
      const elementarySource = map.current.getSource('elementary-source');
      const middleschoolSource = map.current.getSource('middleschool-source');
      const highschoolSource = map.current.getSource('highschool-source');
      
      if (elementarySource) {
        elementarySource.setData(filteredData.elementary);
      }
      if (middleschoolSource) {
        middleschoolSource.setData(filteredData.middleSchool);
      }
      if (highschoolSource) {
        highschoolSource.setData(filteredData.highSchool);
      }
      
    } catch (error) {
      console.error('Error applying school filter:', error);
    }
  }, [currentYear, yearData, applySmartFiltering, colorBySchool, schoolColorMap, schoolColors]);

  // DESKTOP LOGIC APPLIED TO ALL: Clear school filter (same as desktop) - ENHANCED color state management
  const handleClearFilter = useCallback(() => {
    setSelectedSchoolNames(new Set());
    setIsFiltering(false);
    setSelectedSchools([]);
    
    // ENHANCED: Clear color state when clearing all filters
    setSchoolColorMap(new Map());
    setColorBySchool(false);

    if (!map.current || !yearData[currentYear]) return;

    const data = yearData[currentYear];

    try {
      const elementarySource = map.current.getSource('elementary-source');
      const middleschoolSource = map.current.getSource('middleschool-source');
      const highschoolSource = map.current.getSource('highschool-source');

      // DESKTOP APPROACH: Simple source updates without any special handling
      if (elementarySource && data.elementary) {
        elementarySource.setData(data.elementary);
      }
      if (middleschoolSource && data.middleSchool) {
        middleschoolSource.setData(data.middleSchool);
      }
      if (highschoolSource && data.highSchool) {
        highschoolSource.setData(data.highSchool);
      }
      
    } catch (error) {
      console.error('Error clearing school filter:', error);
    }
  }, [currentYear, yearData]);

  // ENHANCED: Load data with intelligent preloading
  const loadYearData = useCallback(async (year) => {
    if (yearData[year]) {
      return yearData[year];
    }

    setIsLoading(true);
    try {
      console.log(`Loading ${year} data...`);
      
      let fetchPaths;
      if (year === '1999') {
        fetchPaths = [
          '/data/1999/elementary_v2.geojson',
          '/data/1999/high_school_v2.geojson',
          '/data/1999/middle_school_v2.geojson'
        ];
      } else if (year === '2003') {
        fetchPaths = [
          '/data/2003/elementary.geojson',
          '/data/2003/high_school.geojson',
          '/data/2003/middle_school.geojson',
          '/data/2003/cluster.geojson'
        ];
      } else if (year === '2008-2009') {
        fetchPaths = [
          '/data/2008-2009/elementary.geojson',
          '/data/2008-2009/high_school.geojson',
          '/data/2008-2009/middle_school.geojson',
          '/data/2008-2009/cluster.geojson'
        ];
      } else if (year === '2013-2014') {
        fetchPaths = [
          '/data/2013-2014/elementary.geojson',
          '/data/2013-2014/high_school.geojson',
          '/data/2013-2014/middle_school.geojson',
          '/data/2013-2014/cluster.geojson'
        ];
      } else if (year === '2018-2019') {
        fetchPaths = [
          '/data/2018-2019/elementary.geojson',
          '/data/2018-2019/high_school.geojson',
          '/data/2018-2019/middle_school.geojson',
          '/data/2018-2019/cluster.geojson'
        ];
      } else if (year === '2024-2025') {
        fetchPaths = [
          '/data/2024-2025/elementary.geojson',
          '/data/2024-2025/high_school.geojson',
          '/data/2024-2025/middle_school.geojson',
          '/data/2024-2025/cluster.geojson'
        ];
      }

      const responses = await Promise.all(fetchPaths.map(path => fetch(path)));
      
      const allDataValid = responses.every(response => response.ok);
      if (!allDataValid) {
        throw new Error(`Failed to load ${year} data`);
      }

      let data;
      if (year === '1999') {
        const [elementaryText, highSchoolText, middleSchoolText] = await Promise.all(
          responses.map(response => response.text())
        );

        data = {
          elementary: JSON.parse(elementaryText),
          highSchool: JSON.parse(highSchoolText),
          middleSchool: JSON.parse(middleSchoolText)
        };
      } else {
        const [elementaryText, highSchoolText, middleSchoolText, clusterText] = await Promise.all(
          responses.map(response => response.text())
        );

        data = {
          elementary: JSON.parse(elementaryText),
          highSchool: JSON.parse(highSchoolText),
          middleSchool: JSON.parse(middleSchoolText),
          clusters: responses.length > 3 && responses[3].ok ? JSON.parse(clusterText) : null
        };
      }

      console.log(`${year} data loaded:`, {
        elementary: data.elementary.features?.length + ' features',
        highSchool: data.highSchool.features?.length + ' features',
        middleSchool: data.middleSchool.features?.length + ' features',
        clusters: data.clusters ? data.clusters.features?.length + ' cluster features' : 'No clusters'
      });

      // Cache the data
      setYearData(prev => ({
        ...prev,
        [year]: data
      }));

      setIsLoading(false);
      return data;
      
    } catch (error) {
      console.error(`Error loading ${year} data:`, error);
      setIsLoading(false);
      return null;
    }
  }, [yearData]); // Include yearData as dependency for checking cached data

  // PRELOAD ADJACENT YEARS: Load nearby years in background for smoother transitions
  const preloadAdjacentYears = useCallback(async (currentYear) => {
    const yearIndex = availableYears.indexOf(currentYear);
    const adjacentYears = [];
    
    // Preload previous and next years
    if (yearIndex > 0) adjacentYears.push(availableYears[yearIndex - 1]);
    if (yearIndex < availableYears.length - 1) adjacentYears.push(availableYears[yearIndex + 1]);
    
    // Load adjacent years in background (don't await)
    adjacentYears.forEach(year => {
      if (!yearData[year]) {
        console.log(`Preloading ${year} data in background...`);
        loadYearData(year).catch(error => {
          console.log(`Background preload failed for ${year}:`, error.message);
        });
      }
    });
  }, [availableYears, yearData, loadYearData]);

  // Fit map to data bounds
  const fitMapToBounds = useCallback((data) => {
    if (!map.current || !data.elementary.features.length) return;

    const bounds = new mapboxgl.LngLatBounds();
    let validCoordinatesFound = false;

    data.elementary.features.forEach(feature => {
      if (feature.geometry.type === 'Polygon') {
        feature.geometry.coordinates[0].forEach(coord => {
          if (Array.isArray(coord) && coord.length >= 2 && 
              typeof coord[0] === 'number' && typeof coord[1] === 'number' &&
              !isNaN(coord[0]) && !isNaN(coord[1]) &&
              coord[0] >= -180 && coord[0] <= 180 &&
              coord[1] >= -90 && coord[1] <= 90) {
            bounds.extend(coord);
            validCoordinatesFound = true;
          }
        });
      }
    });

    if (validCoordinatesFound) {
      map.current.fitBounds(bounds, { 
        padding: 50,
        maxZoom: 12 
      });
    }
  }, []);

  // ENHANCED: Add data sources and layers with preserved viewport
  const addDataToMap = useCallback((data, year) => {
    if (!map.current) return;

    try {
      // PRESERVE VIEWPORT: Store current map state before adding data
      const currentCenter = map.current.getCenter();
      const currentZoom = map.current.getZoom();
      const isInitialLoad = !map.current.getSource('elementary-source');

      console.log('Adding data to map:', { year, isInitialLoad, preserveViewport: !isInitialLoad });

      const displayData = selectedSchoolNames.size > 0 ? 
        applySmartFiltering(data, selectedSchoolNames) : data;

      map.current.addSource('elementary-source', {
        type: 'geojson',
        data: displayData.elementary
      });

      map.current.addSource('highschool-source', {
        type: 'geojson',
        data: displayData.highSchool
      });

      map.current.addSource('middleschool-source', {
        type: 'geojson',
        data: displayData.middleSchool
      });

      // Elementary
      map.current.addLayer({
        id: 'elementary-fill',
        type: 'fill',
        source: 'elementary-source',
        paint: {
          'fill-color': '#3B82F6',
          'fill-opacity': 0.6
        }
      });

      map.current.addLayer({
        id: 'elementary-outline',
        type: 'line',
        source: 'elementary-source',
        paint: {
          'line-color': '#1D4ED8',
          'line-width': 2
        }
      });

      // High School
      map.current.addLayer({
        id: 'highschool-fill',
        type: 'fill',
        source: 'highschool-source',
        paint: {
          'fill-color': '#EF4444',
          'fill-opacity': 0.5
        }
      });

      map.current.addLayer({
        id: 'highschool-outline',
        type: 'line',
        source: 'highschool-source',
        paint: {
          'line-color': '#DC2626',
          'line-width': 1
        }
      });

      // Middle School
      map.current.addLayer({
        id: 'middleschool-fill',
        type: 'fill',
        source: 'middleschool-source',
        paint: {
          'fill-color': '#10B981',
          'fill-opacity': 0.5
        }
      });

      map.current.addLayer({
        id: 'middleschool-outline',
        type: 'line',
        source: 'middleschool-source',
        paint: {
          'line-color': '#059669',
          'line-width': 1
        }
      });

      // SMART VIEWPORT MANAGEMENT: Only fit bounds on initial load or mobile
      if (isInitialLoad && !isMobile) {
        // Initial desktop load - fit to bounds
        fitMapToBounds(data);
      } else if (isInitialLoad && isMobile) {
        // Initial mobile load - use a reasonable default
        map.current.easeTo({
          center: [-77.1, 39.1],
          zoom: 9.5,
          duration: 0
        });
      }
      // For subsequent loads, preserve the current viewport (no action needed)
      
    } catch (error) {
      console.error('Error adding data to map:', error);
    }
  }, [selectedSchoolNames, applySmartFiltering, fitMapToBounds, isMobile]);

  // DESKTOP LOGIC APPLIED TO ALL: Update existing map data (same as desktop)
  const updateMapData = useCallback((data, year) => {
    if (!map.current) return;

    try {
      const displayData = selectedSchoolNames.size > 0 ? 
        applySmartFiltering(data, selectedSchoolNames) : data;
      
      const elementarySource = map.current.getSource('elementary-source');
      const highschoolSource = map.current.getSource('highschool-source');
      const middleschoolSource = map.current.getSource('middleschool-source');
      
      // DESKTOP APPROACH: Simple source updates without any special handling
      if (elementarySource) {
        elementarySource.setData(displayData.elementary);
      }
      if (highschoolSource) {
        highschoolSource.setData(displayData.highSchool);
      }
      if (middleschoolSource) {
        middleschoolSource.setData(displayData.middleSchool);
      }
      
    } catch (error) {
      console.error('Error updating map data:', error);
    }
  }, [selectedSchoolNames, applySmartFiltering]);

  // CRITICAL: Update persistent color state when color mode changes
  useEffect(() => {
    if (colorBySchool && schoolColorMap.size > 0) {
      setPersistentColorState({
        isActive: true,
        colorMap: new Map(schoolColorMap),
        lastUpdated: Date.now()
      });
      console.log('Updated persistent color state:', { active: true, mapSize: schoolColorMap.size });
    } else if (!colorBySchool) {
      setPersistentColorState(prev => ({
        ...prev,
        isActive: false,
        lastUpdated: Date.now()
      }));
      console.log('Deactivated persistent color state');
    }
  }, [colorBySchool, schoolColorMap]);

  // ENHANCED: Reliable color restoration system
  const restoreColorState = useCallback((colorMapToRestore, attempt = 1) => {
    const maxAttempts = 3;
    
    console.log(`Attempting color restoration (attempt ${attempt})`);
    
    if (!map.current || !map.current.isStyleLoaded()) {
      if (attempt < maxAttempts) {
        setTimeout(() => restoreColorState(colorMapToRestore, attempt + 1), 200);
      }
      return;
    }
    
    try {
      // Set color mode and map
      setColorBySchool(true);
      setSchoolColorMap(colorMapToRestore);
      
      // Force immediate color application
      setTimeout(() => {
        if (map.current && map.current.isStyleLoaded() && colorMapToRestore.size > 0) {
          console.log(`Force applying colors (attempt ${attempt})`);
          updateLayerColorsWithColorMap(colorMapToRestore, true);
        }
      }, 50);
      
    } catch (error) {
      console.error(`Color restoration failed on attempt ${attempt}:`, error);
      if (attempt < maxAttempts) {
        setTimeout(() => restoreColorState(colorMapToRestore, attempt + 1), 300);
      }
    }
  }, [updateLayerColorsWithColorMap]);

  // ENHANCED: Handle year changes with smooth transitions and preserved state
  const handleYearChange = useCallback(async (newDisplayYear) => {
    const newDataYear = getDataYear(newDisplayYear);
    if (newDataYear === currentYear || !map.current) return;
    
    // CRITICAL: Store current map state before any changes
    const currentCenter = map.current.getCenter();
    const currentZoom = map.current.getZoom();
    const shouldRestoreColors = persistentColorState.isActive && persistentColorState.colorMap.size > 0;
    const colorMapToRestore = new Map(persistentColorState.colorMap);
    
    console.log('Year change - preserving state:', { 
      center: [currentCenter.lng, currentCenter.lat], 
      zoom: currentZoom,
      shouldRestoreColors,
      colorMapSize: colorMapToRestore.size
    });
    
    // Set loading state to prevent jarring transitions
    setIsLoading(true);
    
    // Update year state immediately for UI feedback
    setCurrentYear(newDataYear);
    
    try {
      const data = await loadYearData(newDataYear);
      if (data) {
        // SMOOTH TRANSITION: Update data without affecting map viewport
        updateMapData(data, newDataYear);
        
        // PRESERVE VIEWPORT: Restore map position after data update
        setTimeout(() => {
          if (map.current) {
            // Only restore viewport if it's significantly different (data loading might have moved it)
            const newCenter = map.current.getCenter();
            const centerDiff = Math.abs(newCenter.lng - currentCenter.lng) + Math.abs(newCenter.lat - currentCenter.lat);
            const zoomDiff = Math.abs(map.current.getZoom() - currentZoom);
            
            if (centerDiff > 0.01 || zoomDiff > 0.5) {
              console.log('Restoring viewport to:', { center: [currentCenter.lng, currentCenter.lat], zoom: currentZoom });
              map.current.easeTo({
                center: [currentCenter.lng, currentCenter.lat],
                zoom: currentZoom,
                duration: 300 // Smooth transition back to original position
              });
            }
          }
        }, 50);
        
        // RESTORE COLORS: Use reliable restoration system
        if (shouldRestoreColors) {
          setTimeout(() => {
            restoreColorState(colorMapToRestore);
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error during year change:', error);
    } finally {
      // Clear loading state after a brief delay to ensure smooth transition
      setTimeout(() => {
        setIsLoading(false);
      }, 200);
    }
  }, [currentYear, getDataYear, loadYearData, updateMapData, persistentColorState, restoreColorState]);

// ENHANCED: Setup tooltip interactions - FIXED to prevent duplicate tooltips
// ENHANCED: Setup tooltip interactions - COMPLETELY FIXED to prevent duplicate tooltips

  // Initialize map with enhanced loading and preloading - FIXED dependencies
  useEffect(() => {
    if (map.current) return;

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

    if (!mapboxgl.accessToken) {
      console.error('Mapbox access token is missing!');
      return;
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v10',
      center: [-77.1, 39.1],
      zoom: 10
    });

    map.current.on('load', async () => {
      console.log('Map loaded, loading initial data...');
      
      const initialData = await loadYearData(currentYear);
      if (initialData) {
        addDataToMap(initialData, currentYear);
        // DO NOT call setupInteractions here - it's handled by separate useEffect
        
        // Start preloading adjacent years after initial load
        setTimeout(() => {
          preloadAdjacentYears(currentYear);
        }, 1000);
      }
    });

    // ENHANCED: Map move handler with better mobile viewport tracking
    map.current.on('move', () => {
      const center = map.current.getCenter();
      const zoom = map.current.getZoom();
      
      setLng(center.lng.toFixed(4));
      setLat(center.lat.toFixed(4));
      setZoom(zoom.toFixed(2));
      
      // SIMPLIFIED: Track current viewport for mobile (no complex preservation logic)
      if (isMobile) {
        setCurrentViewport({
          center: [center.lng, center.lat],
          zoom: zoom
        });
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []); // CRITICAL FIX: Empty dependency array to prevent infinite re-renders

  // SEPARATE EFFECT: Handle preloading without affecting map initialization
  useEffect(() => {
    if (map.current && map.current.isStyleLoaded() && yearData[currentYear]) {
      // Preload adjacent years whenever the current year changes
      const timeoutId = setTimeout(() => {
        preloadAdjacentYears(currentYear);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentYear, preloadAdjacentYears]); // Only depend on currentYear and the preload function

  // SEPARATE EFFECT: Load initial data when map loads - STABLE  
  useEffect(() => {
    const initializeMapData = async () => {
      if (map.current && map.current.isStyleLoaded() && !map.current.getSource('elementary-source')) {
        console.log('Initializing map data for year:', currentYear);
        const initialData = await loadYearData(currentYear);
        if (initialData) {
          addDataToMap(initialData, currentYear);
          // DO NOT call setupInteractions here - it's handled in the separate effect above
        }
      }
    };

    // Only run once when map is loaded and styled
    if (map.current) {
      if (map.current.isStyleLoaded()) {
        initializeMapData();
      } else {
        map.current.once('styledata', initializeMapData);
      }
    }
  }, []); // Empty deps - only run once on mount

  // Toggle layer visibility
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    const elementary = visibleLayers.elementary ? 'visible' : 'none';
    const highSchool = visibleLayers.highSchool ? 'visible' : 'none';
    const middleSchool = visibleLayers.middleSchool ? 'visible' : 'none';

    try {
      if (map.current.getLayer('elementary-fill')) {
        map.current.setLayoutProperty('elementary-fill', 'visibility', elementary);
        map.current.setLayoutProperty('elementary-outline', 'visibility', elementary);
      }

      if (map.current.getLayer('highschool-fill')) {
        map.current.setLayoutProperty('highschool-fill', 'visibility', highSchool);
        map.current.setLayoutProperty('highschool-outline', 'visibility', highSchool);
      }

      if (map.current.getLayer('middleschool-fill')) {
        map.current.setLayoutProperty('middleschool-fill', 'visibility', middleSchool);
        map.current.setLayoutProperty('middleschool-outline', 'visibility', middleSchool);
      }
    } catch (error) {
      console.log('Layers not ready yet:', error.message);
    }
  }, [visibleLayers]);

  const toggleLayer = useCallback((layer) => {
    setVisibleLayers(prev => ({
      ...prev,
      [layer]: !prev[layer]
    }));
  }, []);

  // Track sidebar width changes - STABLE effect with proper cleanup
  useEffect(() => {
    const handleSidebarResize = () => {
      const sidebarElement = document.querySelector('[data-sidebar-width]');
      if (sidebarElement) {
        const width = parseInt(sidebarElement.getAttribute('data-sidebar-width'));
        if (width !== sidebarWidth) { // Only update if actually changed
          setSidebarWidth(width);
        }
      }
    };

    // Set up observer to watch for sidebar width changes
    const observer = new MutationObserver(handleSidebarResize);
    const sidebarElement = document.querySelector('[data-sidebar-width]');
    
    if (sidebarElement) {
      observer.observe(sidebarElement, {
        attributes: true,
        attributeFilter: ['data-sidebar-width']
      });
      handleSidebarResize(); // Initial call
    }

    return () => observer.disconnect();
  }, [sidebarWidth]); // Include sidebarWidth to prevent unnecessary updates

  // MOBILE: Close mobile menu when clicking outside
  useEffect(() => {
    if (!isMobile) return;

    const handleClickOutside = (e) => {
      if (mobileMenuOpen && !e.target.closest('.mobile-menu') && !e.target.closest('.mobile-menu-trigger')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobile, mobileMenuOpen]);

  return (
    <div style={{ 
      position: 'relative', 
      width: '100%', 
      height: '100vh',
      background: 'linear-gradient(135deg, #0f1419 0%, #1a202c 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Inter", sans-serif',
      overflow: 'hidden' // PREVENT: Any scroll issues that might affect UI positioning
    }}>
      {/* MOBILE: Mobile Header - Hidden on Desktop */}
      {isMobile && (
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '60px',
          background: 'linear-gradient(135deg, rgba(15, 20, 25, 0.98) 0%, rgba(26, 32, 44, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          zIndex: 1000, // HIGH Z-INDEX: Ensure it's above everything including map
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          pointerEvents: 'auto' // ENSURE: UI interactions work properly
        }}>
          {/* Mobile Logo */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}>
              <img 
                src="/flag.png" 
                alt="Montgomery County Flag"
                style={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'cover',
                  borderRadius: '8px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div style={{
                width: '32px',
                height: '32px',
                background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                borderRadius: '8px',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
            </div>
            <div>
              <h1 style={{
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                margin: '0',
                letterSpacing: '-0.01em'
              }}>
                MCPS Zoning
              </h1>
              <p style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '11px',
                margin: '1px 0 0 0',
                fontWeight: '400'
              }}>
                {getDisplayYear(currentYear)}
              </p>
            </div>
          </div>
          
          {/* Mobile Header Buttons */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {/* Mobile About Button */}
            <button
              onClick={() => setShowAboutModal(true)}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '8px',
                padding: '8px',
                color: 'rgba(255, 255, 255, 0.9)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
              </svg>
            </button>
            
            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-trigger"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                padding: '8px',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* MOBILE: Mobile Bottom Sheet Menu */}
      {isMobile && mobileMenuOpen && (
        <div 
          className="mobile-menu"
          style={{
            position: 'fixed',
            bottom: '0',
            left: '0',
            right: '0',
            background: 'linear-gradient(135deg, rgba(15, 20, 25, 0.98) 0%, rgba(26, 32, 44, 0.98) 100%)',
            backdropFilter: 'blur(32px)',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 -8px 40px rgba(0, 0, 0, 0.6)',
            zIndex: 1100, // HIGHER Z-INDEX: Above timeline selector (900) and nav (1000)
            maxHeight: '70vh',
            overflowY: 'auto',
            transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {/* Mobile Sheet Header */}
          <div style={{
            padding: '20px 20px 16px 20px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            position: 'sticky',
            top: 0,
            background: 'inherit',
            backdropFilter: 'inherit'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                margin: '0'
              }}>
                Map Controls
              </h3>
              <button
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '6px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            {/* Mobile Tab Navigation */}
            <div style={{
              display: 'flex',
              marginTop: '16px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '10px',
              padding: '4px'
            }}>
              {[
                { id: 'layers', label: 'Layers', icon: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z' },
                { id: 'search', label: 'Address', icon: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z' },
                { id: 'filter', label: 'Schools', icon: 'M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z' },
                { id: 'about', label: 'About', icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === 'about') {
                      setShowAboutModal(true);
                      setMobileMenuOpen(false);
                    } else {
                      setMobileActiveTab(tab.id);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    background: (mobileActiveTab === tab.id && tab.id !== 'about') ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                    border: (mobileActiveTab === tab.id && tab.id !== 'about') ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
                    borderRadius: '6px',
                    color: (mobileActiveTab === tab.id && tab.id !== 'about') ? '#60A5FA' : 'rgba(255, 255, 255, 0.6)',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d={tab.icon}/>
                  </svg>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Tab Content */}
          <div style={{ padding: '0 20px 20px 20px' }}>
            {/* Layer Controls Tab */}
            {mobileActiveTab === 'layers' && (
              <div>
                <h4 style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '16px',
                  fontWeight: '600',
                  margin: '16px 0 12px 0'
                }}>
                  Layer Visibility
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { key: 'elementary', label: 'Elementary Schools', color: '#3B82F6', icon: 'E' },
                    { key: 'highSchool', label: 'High Schools', color: '#EF4444', icon: 'H' },
                    { key: 'middleSchool', label: 'Middle Schools', color: '#10B981', icon: 'M' }
                  ].map(({ key, label, color, icon }) => (
                    <label key={key} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: isFiltering ? 'not-allowed' : 'pointer',
                      padding: '16px',
                      borderRadius: '12px',
                      background: visibleLayers[key] ? 
                        `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)` : 
                        'rgba(255, 255, 255, 0.03)',
                      border: `1px solid ${visibleLayers[key] ? `${color}40` : 'rgba(255, 255, 255, 0.08)'}`,
                      opacity: isFiltering ? 0.5 : 1
                    }}>
                      <input
                        type="checkbox"
                        checked={visibleLayers[key]}
                        onChange={() => !isFiltering && toggleLayer(key)}
                        disabled={isFiltering}
                        style={{ display: 'none' }}
                      />
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        background: visibleLayers[key] ? 
                          (isFiltering ? '#6B7280' : `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`) : 
                          'rgba(255, 255, 255, 0.1)',
                        border: `2px solid ${visibleLayers[key] ? 
                          (isFiltering ? '#6B7280' : color) : 
                          'rgba(255, 255, 255, 0.2)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '16px'
                      }}>
                        {visibleLayers[key] && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                            <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                          </svg>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: isFiltering ? 
                            'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)' : 
                            `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '700',
                          boxShadow: `0 4px 8px ${isFiltering ? '#6B728040' : color + '40'}`
                        }}>
                          {icon}
                        </div>
                        <span style={{ 
                          color: isFiltering ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.9)', 
                          fontSize: '16px', 
                          fontWeight: '600'
                        }}>
                          {label}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

           {/* Address Search Tab */}
{mobileActiveTab === 'search' && (
  <div>
    <h4 style={{
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: '16px',
      fontWeight: '600',
      margin: '16px 0 12px 0'
    }}>
      Address Lookup
    </h4>
    <div style={{ 
      background: 'rgba(255, 255, 255, 0.03)', 
      borderRadius: '12px', 
      padding: '16px',
      // Ensure proper mobile scrolling
      maxHeight: '60vh',
      overflowY: 'auto'
    }}>
      <p style={{
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '14px',
        margin: '0 0 16px 0',
        lineHeight: '1.4'
      }}>
        Find school assignments for any Montgomery County address
      </p>
      
      {/* Actual AddressLookup Component */}
      <AddressLookup
        map={map.current}
        currentYear={currentYear}
        yearData={yearData}
        selectedSchoolNames={selectedSchoolNames}
        isFiltering={isFiltering}
        sidebar={true}
        mobile={true}
        onAddressFound={(marker, assignments) => {
          console.log('Mobile address found:', marker, assignments);
          // Optionally close the mobile menu after successful search
          // setMobileMenuOpen(false);
        }}
        onAddressCleared={() => {
          console.log('Mobile address cleared');
        }}
      />
    </div>
  </div>
)}

            {/* School Filter Tab */}
            {mobileActiveTab === 'filter' && (
              <div>
                <h4 style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '16px',
                  fontWeight: '600',
                  margin: '16px 0 12px 0'
                }}>
                  School Search & Filter
                </h4>
                <div style={{ 
                  background: 'rgba(255, 255, 255, 0.03)', 
                  borderRadius: '12px', 
                  padding: '16px',
                  maxHeight: '60vh',
                  overflowY: 'auto'
                }}>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '14px',
                    margin: '0 0 16px 0',
                    lineHeight: '1.4'
                  }}>
                    Search and filter schools by name or cluster
                  </p>
                  
                  {/* Actual SchoolFilter Component */}
                  <SchoolFilter
                    currentYear={currentYear}
                    yearData={yearData}
                    selectedSchoolNames={selectedSchoolNames}
                    onSchoolsFilter={handleSchoolsFilter}
                    onClearFilter={handleClearFilter}
                    sidebar={true}
                    // ADD COLOR CODING PROPS FOR MOBILE
                    colorBySchool={colorBySchool}
                    onToggleColorMode={toggleColorMode}
                    schoolColorMap={schoolColorMap}
                    schoolColors={schoolColors}
                    selectedSchools={selectedSchools}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DESKTOP: Top Header Bar - Hidden on Mobile */}
      {!isMobile && (
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '64px',
          background: 'linear-gradient(135deg, rgba(15, 20, 25, 0.95) 0%, rgba(26, 32, 44, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          zIndex: 1000, // HIGH Z-INDEX: Ensure it's above everything including map
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          pointerEvents: 'auto' // ENSURE: UI interactions work properly
        }}>
          {/* Left side - Title with flag icon */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
              overflow: 'hidden'
            }}>
              <img 
                src="/flag.png" 
                alt="Montgomery County Flag"
                style={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'cover',
                  borderRadius: '12px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                borderRadius: '12px',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
            </div>
            <div>
              <h1 style={{
                color: 'white',
                fontSize: '22px',
                fontWeight: '700',
                margin: '0',
                letterSpacing: '-0.025em'
              }}>
                MCPS Historical Zoning Tool
              </h1>
              <p style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '13px',
                margin: '2px 0 0 0',
                fontWeight: '400'
              }}>
                Montgomery County Public Schools • 1999-2025
              </p>
            </div>
          </div>
          
          {/* Right side - About button */}
          <button
            onClick={() => setShowAboutModal(true)}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(12px)',
              borderRadius: '10px',
              padding: '10px 16px',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.12)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.08)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.12)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
            </svg>
            About
          </button>
        </div>
      )}

      {/* About Modal */}
      {showAboutModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(8px)',
          zIndex: 1200, // HIGHEST Z-INDEX: Above everything including mobile menu
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '16px' : '20px'
        }}
        onClick={() => setShowAboutModal(false)}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(15, 20, 25, 0.95) 0%, rgba(26, 32, 44, 0.95) 100%)',
              backdropFilter: 'blur(24px)',
              borderRadius: isMobile ? '16px' : '20px',
              padding: isMobile ? '24px' : '32px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
              maxWidth: isMobile ? '100%' : '500px',
              width: '100%',
              maxHeight: isMobile ? '90vh' : '80vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal content - same as before but responsive */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: isMobile ? '20px' : '24px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  <img 
                    src="/flag.png" 
                    alt="Montgomery County Flag"
                    style={{
                      width: '32px',
                      height: '32px',
                      objectFit: 'cover',
                      borderRadius: '8px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div style={{
                    width: '32px',
                    height: '32px',
                    background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                    borderRadius: '8px',
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                </div>
                <h2 style={{
                  color: 'white',
                  fontSize: isMobile ? '18px' : '20px',
                  fontWeight: '700',
                  margin: '0'
                }}>
                  About This Tool
                </h2>
              </div>
              <button
                onClick={() => setShowAboutModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'none';
                  e.target.style.color = 'rgba(255, 255, 255, 0.6)';
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            {/* Modal Content - Responsive */}
            <div style={{
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: '1.6',
              fontSize: isMobile ? '13px' : '14px'
            }}>
              {/* Introduction */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                padding: isMobile ? '12px' : '16px',
                marginBottom: isMobile ? '20px' : '24px'
              }}>
                <p style={{ 
                  margin: '0', 
                  fontSize: isMobile ? '14px' : '15px', 
                  lineHeight: '1.5' 
                }}>
                  The <strong style={{ color: 'white' }}>MCPS Historical Zoning Tool</strong> provides an interactive exploration of Montgomery County Public Schools' boundary changes over more than 25 years, from 1999 to 2025.
                </p>
              </div>
              
              {/* Features Section */}
              <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
                <h3 style={{ 
                  color: 'white', 
                  fontSize: isMobile ? '15px' : '16px', 
                  fontWeight: '700', 
                  margin: '0 0 12px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#3B82F6">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  Features
                </h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? '8px' : '10px'
                }}>
                  {[
                    { icon: '#3B82F6', title: 'Time Navigation:', desc: 'Explore 6 different academic years (1999, 2003, 2008, 2013, 2018, 2024)' },
                    { icon: '#10B981', title: 'School Filtering:', desc: 'Select specific schools to highlight boundary changes' },
                    { icon: '#EF4444', title: 'Address Lookup:', desc: 'Find school assignments for any Montgomery County address' },
                    { icon: '#8B5CF6', title: 'Color Coding:', desc: 'View boundaries by school type or individual school colors' },
                    { icon: '#F59E0B', title: 'Interactive Tooltips:', desc: 'Hover (desktop) or tap (mobile) boundaries for detailed information' }
                  ].map((feature, index) => (
                    <div key={index} style={{ 
                      padding: isMobile ? '6px 10px' : '8px 12px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.06)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{ 
                          width: '6px', 
                          height: '6px', 
                          background: feature.icon, 
                          borderRadius: '50%',
                          marginTop: '6px',
                          flexShrink: 0
                        }}></div>
                        <div>
                          <strong style={{ color: 'white' }}>{feature.title}</strong> {feature.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* How to Use Section */}
              <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
                <h3 style={{ 
                  color: 'white', 
                  fontSize: isMobile ? '15px' : '16px', 
                  fontWeight: '700', 
                  margin: '0 0 12px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#10B981">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  How to Use
                </h3>
                <div style={{
                  background: 'rgba(16, 185, 129, 0.08)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <p style={{ margin: '0', fontSize: isMobile ? '12px' : '13px' }}>
                    {isMobile ? 
                      'Use the menu button to access controls. Toggle layer visibility, search for schools, or lookup addresses. Tap any school boundary to see detailed information including area and cluster assignments.' :
                      'Use the time slider at the bottom to navigate between years. Toggle layer visibility in the left sidebar, search for specific schools, or lookup addresses. Hover over any boundary to see detailed information including school names, clusters, and areas.'
                    }
                  </p>
                </div>
              </div>

              {/* Developer Section */}
              <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
                <h3 style={{ 
                  color: 'white', 
                  fontSize: isMobile ? '15px' : '16px', 
                  fontWeight: '700', 
                  margin: '0 0 12px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#F59E0B">
                    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.1 3.9 23 5 23H19C20.1 23 21 22.1 21 21V9M12 8C13.68 8 16 9.5 16 12.5C16 14.18 15.22 15.68 14 16.68L14 18H10V16.68C8.78 15.68 8 14.18 8 12.5C8 9.5 10.32 8 12 8Z"/>
                  </svg>
                  About the Developer
                </h3>
                <div style={{
                  background: 'rgba(245, 158, 11, 0.08)',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: isMobile ? '12px' : '13px' }}>
                    Created by <strong style={{ color: '#FBBF24' }}>Kevin Klein</strong>, a Montgomery County resident passionate about data visualization and community tools.
                  </p>
                  <div style={{ 
                    display: 'flex', 
                    gap: '12px', 
                    fontSize: isMobile ? '11px' : '12px',
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <a 
                      href="mailto:kevinklein333@gmail.com" 
                      style={{ 
                        color: '#FBBF24', 
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                      </svg>
                      Email
                    </a>
                    <a 
                      href="https://www.linkedin.com/in/kevinkleinads" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#FBBF24', 
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>

              {/* Data Attribution & Disclaimer */}
              <div>
                <h3 style={{ 
                  color: 'white', 
                  fontSize: isMobile ? '15px' : '16px', 
                  fontWeight: '700', 
                  margin: '0 0 12px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#EF4444">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                  </svg>
                  Important Notice
                </h3>
                <div style={{
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: isMobile ? '12px' : '13px' }}>
                    <strong style={{ color: '#F87171' }}>Data Source:</strong> Boundary data provided by Montgomery County Public Schools (MCPS).
                  </p>
                  <p style={{ margin: '0 0 8px 0', fontSize: isMobile ? '12px' : '13px' }}>
                    <strong style={{ color: '#F87171' }}>Disclaimer:</strong> This tool is <strong>not affiliated with MCPS</strong> and is an independent community project. Historical data is for research and visualization purposes only.
                  </p>
                  <p style={{ margin: '0', fontSize: isMobile ? '12px' : '13px' }}>
                    <strong style={{ color: '#F87171' }}>Current Assignments:</strong> For official school assignments, please use the{' '}
                    <a 
                      href="https://ww2.montgomeryschoolsmd.org/schools/index.aspx" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#F87171', 
                        textDecoration: 'underline',
                        fontWeight: '600'
                      }}
                    >
                      MCPS School Locator
                    </a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Map container - Responsive padding with isolation */}
      <div ref={mapContainer} style={{ 
        width: '100%', 
        height: '100%', 
        paddingTop: isMobile ? '60px' : '64px',
        position: 'relative',
        zIndex: 1, // LOW Z-INDEX: Keep map in background layer
        isolation: 'isolate' // CSS ISOLATION: Create new stacking context for map
      }} />
      
      {/* Compact Hover Information Panel - Only on Desktop */}
      <HoverInfo
  map={map.current}
  currentYear={currentYear}
  yearData={yearData}
  findClusterForPoint={findClusterForPoint}
  isEnabled={true}
  isMobile={isMobile}
/>
      
      {/* DESKTOP: Sidebar Component - Hidden on Mobile */}
      {!isMobile && (
        <Sidebar
          currentYear={currentYear}
          yearData={yearData}
          visibleLayers={visibleLayers}
          onToggleLayer={toggleLayer}
          selectedSchoolNames={selectedSchoolNames}
          selectedSchools={selectedSchools}
          isFiltering={isFiltering}
          onSchoolsFilter={handleSchoolsFilter}
          onClearFilter={handleClearFilter}
          colorBySchool={colorBySchool}
          onToggleColorMode={toggleColorMode}
          schoolColorMap={schoolColorMap}
          schoolColors={schoolColors}
          map={map.current}
        />
      )}

      {/* MOBILE: Floating Year Indicator - Top Right */}
      {isMobile && (
        <div style={{
          position: 'absolute',
          top: '70px',
          right: '16px',
          background: 'rgba(15, 20, 25, 0.9)',
          backdropFilter: 'blur(12px)',
          borderRadius: '10px',
          padding: '8px 12px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          zIndex: 900, // HIGH Z-INDEX: Ensure it's above map but below modals
          pointerEvents: 'none' // LOCKED: No interaction needed, just visual indicator
        }}>
          <div style={{
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            {getDisplayYear(currentYear)}
          </div>
          <div style={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '10px',
            textAlign: 'center',
            marginTop: '2px'
          }}>
            Academic Year
          </div>
        </div>
      )}

      {/* Time Slider - Responsive positioning */}
      <div style={{
        position: 'absolute',
        bottom: isMobile ? '16px' : '16px',
        left: `${timelineLeftMargin}px`,
        right: isMobile ? '16px' : '24px',
        zIndex: 900, // HIGH Z-INDEX: Ensure it's above map but below modals
        display: 'flex',
        justifyContent: 'center',
        transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: 'auto' // ENSURE: UI interactions work properly
      }}>
        <div style={{ 
          maxWidth: isMobile ? '100%' : '500px', 
          width: '100%' 
        }}>
          <TimeSlider
            availableYears={displayYears}
            currentYear={getDisplayYear(currentYear)}
            onYearChange={handleYearChange}
            isLoading={isLoading}
            compact={isMobile}
            semiTransparent={isMobile}
          />
        </div>
      </div>

      {/* Coordinates Info - Only on Desktop */}
      {!isMobile && (
        <div style={{
          position: 'absolute',
          bottom: '16px',
          right: '24px',
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(12px)',
          borderRadius: '8px',
          padding: '6px 10px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          zIndex: 800, // HIGH Z-INDEX: Ensure it's above map
          fontSize: '9px',
          pointerEvents: 'none' // LOCKED: No interaction needed, just visual info
        }}>
          <div style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontFamily: 'JetBrains Mono, Consolas, monospace',
            fontWeight: '500',
            letterSpacing: '0.025em',
            lineHeight: '1.2'
          }}>
            <div>{lng}°, {lat}° • {zoom}x</div>
          </div>
        </div>
      )}

      {/* MOBILE: Touch Gestures Hint - Only show on first load */}
      {isMobile && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(15, 20, 25, 0.9)',
          backdropFilter: 'blur(12px)',
          borderRadius: '12px',
          padding: '12px 16px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.4)',
          zIndex: 8,
          display: 'none', // Hidden by default - could be shown on first visit
          textAlign: 'center'
        }}>
          <div style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '12px',
            fontWeight: '500',
            marginBottom: '4px'
          }}>
            💡 Tip
          </div>
          <div style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '11px',
            lineHeight: '1.3'
          }}>
            Pinch to zoom • Drag to pan<br/>
            Tap boundaries for info • Use menu for controls
          </div>
        </div>
      )}
    </div>
  );
};

export default MapContainer;